import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Loader2, Search, Layers, Eye, EyeOff, Info, Download, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

const STYLE_STREETS = 'mapbox://styles/mapbox/outdoors-v12';
const STYLE_SATELLITE = 'mapbox://styles/mapbox/satellite-streets-v12';

const STATUS_LABEL = {
  pending_ai_review: 'Pending',
  high_confidence: 'High Confidence',
  community_verified: 'Community Verified',
  expert_verified: 'Expert Verified',
  research_verified: 'Research Grade',
};

const STATUS_COLOR = {
  pending_ai_review: '#9CA3AF',
  high_confidence: '#E9C46A',
  community_verified: '#95D5B2',
  expert_verified: '#40916C',
  research_verified: '#2A9D8F',
};

// Debounce helper for map moves
function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

export default function BiodiversityMap() {
  const nav = useNavigate();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [observations, setObservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [active, setActive] = useState(null);
  const [style, setStyle] = useState('streets'); // streets | satellite
  const [showHeat, setShowHeat] = useState(false);
  const [heatBuckets, setHeatBuckets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishForm, setPublishForm] = useState({ title: '', description: '' });
  const [publishing, setPublishing] = useState(false);

  const doExport = (format) => {
    if (!mapRef.current) return;
    const b = mapRef.current.getBounds();
    const sw = b.getSouthWest(), ne = b.getNorthEast();
    const params = new URLSearchParams({
      sw_lat: sw.lat, sw_lng: sw.lng, ne_lat: ne.lat, ne_lng: ne.lng,
    });
    if (statusFilter !== 'all') params.set('status', statusFilter);
    const path = format === 'csv' ? 'csv' : format === 'geojson' ? 'geojson' : 'dwc-a';
    const url = `${api.defaults.baseURL}/exports/${path}?${params.toString()}`;
    window.open(url, '_blank');
    setExportOpen(false);
  };

  const doPublish = async () => {
    if (!mapRef.current || !publishForm.title.trim() || publishing) return;
    setPublishing(true);
    const b = mapRef.current.getBounds();
    const sw = b.getSouthWest(), ne = b.getNorthEast();
    const body = {
      title: publishForm.title.trim(),
      description: publishForm.description.trim() || undefined,
      sw_lat: sw.lat, sw_lng: sw.lng, ne_lat: ne.lat, ne_lng: ne.lng,
    };
    if (statusFilter !== 'all') body.status = statusFilter;
    try {
      const { data } = await api.post('/gbif/publications', body);
      const pubId = data.publication.publication_id;
      toast.success('Publication created');
      setPublishOpen(false);
      setPublishForm({ title: '', description: '' });
      // Navigate to the public citation page.
      nav(`/gbif/${pubId}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Publish failed');
    } finally { setPublishing(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await api.get('/biodiversity/stats');
      setStats(data);
    } catch { /* non-blocking */ }
  };

  const loadForBounds = useCallback(async () => {
    if (!mapRef.current) return;
    setLoading(true);
    try {
      const b = mapRef.current.getBounds();
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const params = new URLSearchParams({
        sw_lat: sw.lat, sw_lng: sw.lng, ne_lat: ne.lat, ne_lng: ne.lng, limit: 500,
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const { data } = await api.get(`/biodiversity/bbox?${params.toString()}`);
      setObservations(data.observations || []);

      if (showHeat) {
        const zoom = mapRef.current.getZoom();
        const bucket_deg = zoom < 4 ? 5 : zoom < 6 ? 2 : zoom < 8 ? 1 : zoom < 10 ? 0.5 : 0.2;
        const { data: h } = await api.get(`/biodiversity/heatmap?${params.toString()}&bucket_deg=${bucket_deg}`);
        setHeatBuckets(h.buckets || []);
      } else {
        setHeatBuckets([]);
      }
    } catch (err) {
      if (!err?.__silenced) {
        toast.error('Failed to load observations');
        err.__silenced = true;
      }
    } finally { setLoading(false); }
  }, [statusFilter, showHeat]);

  // init map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!mapboxgl.accessToken) {
      toast.error('Mapbox token missing. Contact support.');
      setLoading(false); return;
    }
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: style === 'satellite' ? STYLE_SATELLITE : STYLE_STREETS,
      center: [0, 20], zoom: 1.8, minZoom: 1, maxZoom: 18, projection: 'globe',
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.on('style.load', () => { try { map.setFog({}); } catch {} });
    map.on('load', () => {
      setReady(true);
      mapRef.current = map;
      loadForBounds();
    });
    const debounced = debounce(() => loadForBounds(), 350);
    map.on('moveend', debounced);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // reload on filter / heat toggle
  useEffect(() => { if (ready) loadForBounds(); }, [ready, loadForBounds]);

  // stats once
  useEffect(() => { loadStats(); }, []);

  // draw markers whenever data changes
  useEffect(() => {
    if (!mapRef.current) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    // heatmap buckets first (behind pins)
    heatBuckets.forEach(b => {
      const el = document.createElement('div');
      const size = Math.min(64, 12 + Math.log2(Math.max(b.count, 1)) * 8);
      el.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;background:radial-gradient(circle,rgba(233,196,106,0.55) 0%,rgba(233,196,106,0.0) 70%);pointer-events:none;`;
      const m = new mapboxgl.Marker(el).setLngLat([b.lng, b.lat]).addTo(mapRef.current);
      markersRef.current.push(m);
    });
    observations.forEach(obs => {
      if (obs.latitude == null || obs.longitude == null) return;
      const el = document.createElement('div');
      const color = STATUS_COLOR[obs.verification_status] || STATUS_COLOR.pending_ai_review;
      el.setAttribute('data-testid', `obs-pin-${obs.observation_id}`);
      el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #04120c;box-shadow:0 0 0 2px ${color}44;cursor:pointer;`;
      el.addEventListener('click', (ev) => { ev.stopPropagation(); setActive(obs); });
      const m = new mapboxgl.Marker(el).setLngLat([obs.longitude, obs.latitude]).addTo(mapRef.current);
      markersRef.current.push(m);
    });
  }, [observations, heatBuckets]);

  // style swap
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setStyle(style === 'satellite' ? STYLE_SATELLITE : STYLE_STREETS);
  }, [style]);

  return (
    <div className="relative w-full h-[100dvh] bg-[#04120c]" data-testid="biodiversity-map-root">
      <div ref={containerRef} className="absolute inset-0" />

      {/* TOP BAR */}
      <div className="absolute top-3 left-3 right-3 z-10 flex items-center gap-2 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full glass border border-white/10">
          <Compass className="w-4 h-4 text-primary-light" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-primary-light">Biodiversity Network</span>
        </div>
        {stats && (
          <div className="pointer-events-auto hidden sm:flex items-center gap-3 px-3 py-2 rounded-full glass border border-white/10 text-xs text-white/80">
            <span data-testid="stat-total">{stats.total_observations} obs</span>
            <span className="text-white/30">·</span>
            <span data-testid="stat-verified">{stats.verified_observations} verified</span>
            <span className="text-white/30">·</span>
            <span data-testid="stat-species">{stats.distinct_species_public} species</span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 pointer-events-auto">
          <button
            data-testid="btn-toggle-heat"
            onClick={() => setShowHeat(v => !v)}
            className="px-3 py-2 rounded-full glass border border-white/10 text-xs hover:bg-white/10 flex items-center gap-1"
          >
            {showHeat ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            Heat
          </button>
          <button
            data-testid="btn-toggle-style"
            onClick={() => setStyle(s => s === 'satellite' ? 'streets' : 'satellite')}
            className="px-3 py-2 rounded-full glass border border-white/10 text-xs hover:bg-white/10 flex items-center gap-1"
          >
            <Layers className="w-3.5 h-3.5" />
            {style === 'satellite' ? 'Streets' : 'Satellite'}
          </button>
          <div className="relative">
            <button
              data-testid="btn-open-export"
              onClick={() => setExportOpen(v => !v)}
              className="px-3 py-2 rounded-full glass border border-white/10 text-xs hover:bg-white/10 flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" /> Export
            </button>
            {exportOpen && (
              <div
                data-testid="export-menu"
                className="absolute right-0 mt-2 w-52 p-2 rounded-2xl glass border border-white/10 bg-black/70 backdrop-blur-xl space-y-1 z-20"
              >
                <button
                  data-testid="btn-export-csv"
                  onClick={() => doExport('csv')}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-xs"
                >CSV (current bbox)</button>
                <button
                  data-testid="btn-export-geojson"
                  onClick={() => doExport('geojson')}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-xs"
                >GeoJSON (current bbox)</button>
                <button
                  data-testid="btn-export-dwca"
                  onClick={() => doExport('dwca')}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-xs"
                >Darwin Core Archive (ZIP)</button>
                <div className="border-t border-white/10 my-1" />
                <button
                  data-testid="btn-open-publish"
                  onClick={() => { setExportOpen(false); setPublishOpen(true); }}
                  className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/10 text-xs flex items-center gap-2"
                >
                  <BookOpen className="w-3.5 h-3.5 text-primary-light" />
                  <span>Publish to GBIF…</span>
                </button>
                <p className="px-3 py-1.5 text-[9px] uppercase tracking-widest text-white/40">Verified public obs only · max 10k rows</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FILTER CHIPS */}
      <div className="absolute top-16 left-3 z-10 flex flex-wrap gap-1.5 max-w-[80%]">
        {['all', 'high_confidence', 'community_verified', 'expert_verified', 'research_verified'].map(s => (
          <button
            key={s}
            data-testid={`filter-status-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider border transition-colors ${
              statusFilter === s
                ? 'bg-primary text-white border-primary'
                : 'glass border-white/10 text-white/70 hover:text-white'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {/* LOADING */}
      {loading && (
        <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-full glass border border-white/10 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-light" />
          <span>Loading…</span>
        </div>
      )}

      {/* LEGEND */}
      <div className="absolute bottom-4 right-4 z-10 p-3 rounded-2xl glass border border-white/10 text-[10px] uppercase tracking-widest space-y-1 hidden sm:block">
        {Object.entries(STATUS_LABEL).slice(1).map(([k, label]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLOR[k] }} />
            <span className="text-white/80">{label}</span>
          </div>
        ))}
      </div>

      {/* PUBLISH TO GBIF MODAL */}
      <AnimatePresence>
        {publishOpen && (
          <motion.div
            data-testid="publish-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !publishing && setPublishOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-md p-5 rounded-3xl glass border border-white/10 bg-black/70"
            >
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-primary-light" />
                <h2 className="font-serif text-xl">Publish to GBIF</h2>
              </div>
              <p className="text-xs text-white/70 mb-4">
                Freeze the current map view as a citable Darwin Core Archive. Anyone with the
                link can view the citation and download the exact dataset — perfect for papers,
                NGO reports, and university partnerships.
              </p>
              <label className="block mb-3">
                <span className="text-[10px] uppercase tracking-widest text-white/60">Title</span>
                <input
                  data-testid="publish-title"
                  type="text" required maxLength={200}
                  value={publishForm.title}
                  onChange={e => setPublishForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Boreal Bird Snapshot — Feb 2026"
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm outline-none focus:border-primary/60"
                />
              </label>
              <label className="block mb-4">
                <span className="text-[10px] uppercase tracking-widest text-white/60">Description (optional)</span>
                <textarea
                  data-testid="publish-description"
                  maxLength={2000} rows={3}
                  value={publishForm.description}
                  onChange={e => setPublishForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Scope, methodology, contact — anything that helps future citers."
                  className="mt-1 w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm outline-none focus:border-primary/60 resize-none"
                />
              </label>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-4">
                Filter: current map bounds
                {statusFilter !== 'all' && <> · status <span className="text-primary-light">{statusFilter}</span></>}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setPublishOpen(false)}
                  disabled={publishing}
                  className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs disabled:opacity-50"
                >Cancel</button>
                <button
                  data-testid="btn-confirm-publish"
                  onClick={doPublish}
                  disabled={publishing || !publishForm.title.trim()}
                  className="px-4 py-2 rounded-full bg-primary text-white text-xs flex items-center gap-1 disabled:opacity-50"
                >
                  {publishing ? (
                    <>
                      <span className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      <span>Publishing…</span>
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-3.5 h-3.5" />
                      <span>Publish</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* DETAIL DRAWER */}
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="obs-detail-drawer"
            initial={{ y: 240, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 240, opacity: 0 }}
            transition={{ type: 'spring', damping: 24, stiffness: 220 }}
            className="absolute bottom-0 left-0 right-0 z-20 p-4 pb-8 rounded-t-3xl glass border-t border-white/10 bg-black/70 backdrop-blur-xl"
          >
            <div className="flex items-start gap-3">
              {active.image_path && (
                <img
                  src={`${api.defaults.baseURL}/files/${active.image_path}`}
                  alt=""
                  className="w-20 h-20 rounded-2xl object-cover border border-white/10"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span
                    className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest"
                    style={{ background: `${STATUS_COLOR[active.verification_status]}22`, color: STATUS_COLOR[active.verification_status] }}
                  >
                    {STATUS_LABEL[active.verification_status] || active.verification_status}
                  </span>
                  {active.coords_obscured && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-white/10 text-white/70">Obscured</span>
                  )}
                  {active.is_sensitive && (
                    <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-red-500/20 text-red-300">Sensitive</span>
                  )}
                </div>
                <h3 className="font-serif text-lg leading-tight truncate">{active.species_common_name}</h3>
                <p className="italic text-xs text-white/60 truncate">{active.species_scientific_name}</p>
                {active.area && <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{active.area}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid="btn-view-obs-detail"
                    onClick={() => nav(`/observations/${active.observation_id}`)}
                    className="px-3 py-1.5 rounded-full bg-primary text-white text-xs flex items-center gap-1"
                  >
                    <Info className="w-3 h-3" /> Details
                  </button>
                  <button
                    onClick={() => setActive(null)}
                    className="px-3 py-1.5 rounded-full glass border border-white/10 text-xs"
                  >Close</button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
